import type { DialogProps } from "./DialogTypes";

import { Button } from "../Button";
import { Heading } from "../Heading";
import { Close } from "../icons";
import { PageHeader } from "../PageHeader";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import { useDialogStyles } from "./useDialogStyles";

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { View, StyleSheet, Pressable } from "react-native";

export { DialogSize } from "./DialogTypes";
export type { DialogProps } from "./DialogTypes";

export function Dialog({
  visible,
  onClose,
  headerText,
  image,
  title,
  subtitle,
  buttons,
  sx,
  accessibilityLabelIcon = "Close",
  size,
  headingAriaLevel = 2,
  portaled = false,
}: DialogProps) {
  const dialogRef = useRef<View>(null);
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null);
  const previousFocusRef = useRef<Element | null>(null);
  const titleId = `dialog-title-${useId()}`;
  const subtitleId = `dialog-subtitle-${useId()}`;
  const theme = useTheme();
  const shared = useDialogStyles(size);

  // Portal: create a container in document.body for rendering above all content
  useEffect(() => {
    if (!portaled) return;
    const el = document.createElement("div");
    el.setAttribute("role", "presentation");
    document.body.appendChild(el);
    setPortalEl(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [portaled]);

  // Combined effect: focus, focus trap, inert, and restore
  useEffect(() => {
    if (!visible || typeof document === "undefined") return;

    previousFocusRef.current = document.activeElement;

    // When portaled, a parent modal's focus trap may have inerted this dialog's
    // portal container before the dialog opened. Remove it so the dialog is interactive.
    if (portaled && portalEl?.inert) {
      portalEl.inert = false;
    }

    const dialogEl = dialogRef.current as unknown as HTMLElement | null;

    // Focus the title heading. When `title` is set, Heading uses nativeID={titleId} (avoids
    // matching a PageHeader heading). Otherwise assign titleId to the first heading.
    const heading = title
      ? (dialogEl?.querySelector?.(`#${CSS.escape(titleId)}`) as HTMLElement | null)
      : (dialogEl?.querySelector?.('[role="heading"]') as HTMLElement | null);
    if (heading) {
      if (!title) {
        heading.id = titleId;
      }
      heading.tabIndex = -1;
      heading.focus();
    } else {
      dialogEl?.focus?.();
    }

    // Focus trap: redirect focus back if it escapes the dialog
    const trapFocus = (e: FocusEvent) => {
      if (dialogEl && !dialogEl.contains(e.target as Node)) {
        e.stopPropagation();
        dialogEl.focus();
      }
    };
    document.addEventListener("focusin", trapFocus);

    // Inert: walk from overlay to body, setting inert on siblings at each level
    const inertedElements: HTMLElement[] = [];
    const overlayEl = dialogEl?.parentElement;
    if (overlayEl) {
      let current: HTMLElement | null = overlayEl;
      while (current && current !== document.body) {
        const parent: HTMLElement | null = current.parentElement;
        if (parent) {
          for (const sibling of Array.from(parent.children)) {
            if (sibling === current) continue;
            if (!(sibling as HTMLElement).inert) {
              (sibling as HTMLElement).inert = true;
              inertedElements.push(sibling as HTMLElement);
            }
          }
        }
        current = parent;
      }
    }

    // Cleanup: remove trap, restore inert, restore focus
    return () => {
      document.removeEventListener("focusin", trapFocus);

      for (const el of inertedElements) {
        el.inert = false;
      }

      const prev = previousFocusRef.current as HTMLElement | null;
      if (prev && document.contains(prev)) {
        prev.focus?.();
      }
      previousFocusRef.current = null;
    };
  }, [visible, titleId, portaled, portalEl, title]);

  // Close on Escape key
  useEffect(() => {
    if (!visible || typeof document === "undefined") return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  const webStyles = useMemo(
    () =>
      StyleSheet.create({
        overlay: {
          position: "fixed" as never,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10000,
          padding: theme.spacing.s5,
        },
        backdrop: {
          position: "absolute",
          inset: 0,
          backgroundColor: theme.colors.utilityOverlay,
        },
        dialogMaxHeight: {
          maxHeight: "100%",
        },
      }),
    [theme],
  );

  if (!visible) return null;

  const dialogAriaProps = (() => {
    const describedBy = subtitle ? { "aria-describedby": subtitleId } : {};
    if (headerText) {
      return { "aria-labelledby": titleId, ...describedBy };
    }
    if (title) {
      return { "aria-labelledby": titleId, ...describedBy };
    }
    if (subtitle) {
      return { "aria-labelledby": subtitleId };
    }
    return {};
  })();

  const content = (
    <View style={webStyles.overlay}>
      <Pressable
        style={webStyles.backdrop}
        onPress={onClose}
        focusable={false}
        accessible={false}
        accessibilityElementsHidden={true}
        tabIndex={-1 as never}
      />
      <View
        ref={dialogRef}
        style={[shared.dialogContainer, webStyles.dialogMaxHeight, sx]}
        role="dialog"
        aria-modal={true}
        {...dialogAriaProps}
        tabIndex={-1 as never}
      >
        {headerText && (
          <View style={shared.header}>
            <PageHeader
              title={headerText}
              showBorder={false}
              headingAriaLevel={headingAriaLevel}
              headerRight={
                <Button
                  ariaLabel={accessibilityLabelIcon}
                  onPress={onClose}
                  variant="tertiary"
                  iconBefore={
                    <Close aria-hidden size="md" color={theme.colors.foregroundActionStrong} />
                  }
                  iconOnly={true}
                />
              }
            />
          </View>
        )}
        <View style={shared.contentContainer}>
          {image && <View style={shared.imageContainer}>{image}</View>}
          <View style={[shared.textContainer, { width: "100%" }]}>
            {title && (
              <Heading
                level={4}
                nativeID={titleId}
                sx={shared.title}
                aria-level={Math.min(headingAriaLevel + 1, 6)}
              >
                {title}
              </Heading>
            )}
            {subtitle && (
              <Text level={2} nativeID={subtitleId} sx={shared.subtitle}>
                {subtitle}
              </Text>
            )}
          </View>
        </View>
        {buttons && (
          <View style={shared.buttonContainer}>
            {buttons.map((buttonProps, index) => (
              <Button key={index} {...buttonProps} />
            ))}
          </View>
        )}
      </View>
    </View>
  );

  if (portaled && portalEl) {
    return createPortal(content, portalEl);
  }

  return content;
}
